import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Anexos from "../components/anexos";
import ExamesSolicitacao from "../components/examesSolicitacao";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { ProntuarioGeralistaProps } from "../interfaces/prontuarioGeralista.interface";
import { ProntuarioGeralistaFormData, getInitialData } from "../models/prontuarioGeralista.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const ProntuarioGeralista = ({ onDataChange, initialData, onSave, onPrint, atendimentoId, pacienteId, empresaId }: ProntuarioGeralistaProps) => {
  const [formData, setFormData] = useState<ProntuarioGeralistaFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);
  const [quantidadeExames, setQuantidadeExames] = useState<number>(0);
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Atualizar formData quando initialData mudar (ex: após carregar dados do backend)
  useEffect(() => {
    if (initialData) {
      console.log('🔄 [ProntuarioGeralista] Atualizando formData com initialData:', {
        id: initialData.id,
        hasExamesSolicitados: !!initialData.examesSolicitados,
        examesCount: initialData.examesSolicitados?.length || 0,
        exames: initialData.examesSolicitados
      });
      
      const updatedData = getInitialData(initialData);
      
      console.log('✅ [ProntuarioGeralista] Dados processados:', {
        examesSolicitados: updatedData.examesSolicitados,
        examesCount: updatedData.examesSolicitados?.length || 0
      });
      
      // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
      if (!updatedData.status_atendimento) {
        updatedData.status_atendimento = StatusAtendimento.NOVO;
      }
      setFormData(updatedData);
      if (initialData.status_atendimento) {
        setStatusAtendimento(initialData.status_atendimento);
      } else {
        setStatusAtendimento(StatusAtendimento.NOVO);
      }
      if (initialData.id) {
        setProtocoloId(initialData.id);
      }
      // Atualizar quantidadeExames quando initialData for carregado
      if (updatedData.examesSolicitados && updatedData.examesSolicitados.length > 0) {
        setQuantidadeExames(updatedData.examesSolicitados.length);
      }
    }
  }, [initialData]);

  // Carregar exames quando protocoloId estiver disponível
  useEffect(() => {
    const loadExames = async () => {
      if (!protocoloId) return;
      
      // Verificar se já temos exames no formData
      const currentExames = formData.examesSolicitados;
      if (currentExames && currentExames.length > 0) {
        console.log('ℹ️ [ProntuarioGeralista] Exames já carregados no formData:', currentExames);
        return;
      }
      
      try {
        console.log('🔍 [ProntuarioGeralista] Carregando exames para protocolo:', protocoloId);
        const response = await api.get(`/api/protocolo-prontuario-generalista/${protocoloId}`);
        
        if (response?.result) {
          const exames = extractExamesFromResponse(response.result);
          if (exames && exames.length > 0) {
            console.log('✅ [ProntuarioGeralista] Exames carregados do backend:', exames);
            setFormData((prevData) => ({
              ...prevData,
              examesSolicitados: exames
            }));
            setQuantidadeExames(exames.length);
          } else {
            console.log('ℹ️ [ProntuarioGeralista] Nenhum exame encontrado para este protocolo');
            setQuantidadeExames(0);
          }
        }
      } catch (error) {
        console.error('❌ [ProntuarioGeralista] Erro ao carregar exames:', error);
        setQuantidadeExames(0);
      }
    };

    loadExames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocoloId]);

  // Atualizar quantidadeExames quando formData.examesSolicitados mudar
  useEffect(() => {
    const examesCount = Array.isArray(formData.examesSolicitados) ? formData.examesSolicitados.length : 0;
    if (examesCount > 0 && examesCount !== quantidadeExames) {
      console.log('🔄 [ProntuarioGeralista] Atualizando quantidadeExames:', examesCount);
      setQuantidadeExames(examesCount);
    }
  }, [formData.examesSolicitados, quantidadeExames]);

  // Função auxiliar para extrair exames da resposta
  const extractExamesFromResponse = (data: any): string[] => {
    if (!data?.examesSolicitados) return [];
    
    if (Array.isArray(data.examesSolicitados)) {
      if (data.examesSolicitados.length > 0) {
        const firstItem = data.examesSolicitados[0];
        if (typeof firstItem === 'string') {
          return data.examesSolicitados;
        }
        if (firstItem?.exame?.nome) {
          return data.examesSolicitados.map((item: any) => item.exame.nome);
        }
        if (firstItem?.nome) {
          return data.examesSolicitados.map((item: any) => item.nome);
        }
      }
    }
    
    return [];
  };

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: ProntuarioGeralistaFormData) => {
      // Extrair examesSolicitados antes de salvar o protocolo
      const examesSolicitados = data.examesSolicitados || [];
      
      // Remover examesSolicitados do data antes de salvar (não faz parte do schema do protocolo)
      const { examesSolicitados: _, ...dadosProtocolo } = data;
      
      // Usar protocoloId do estado se disponível, caso contrário usar initialData
      const currentProtocoloId = protocoloId || initialData?.id;
      
      console.log('💾 [ProntuarioGeralista] Salvando protocolo:', {
        currentProtocoloId,
        protocoloId,
        initialDataId: initialData?.id,
        atendimentoId,
        pacienteId: pacienteId || initialData?.id_paciente
      });
      
      const response = await saveFormProtocolo({
        endpoint: '/api/protocolo-prontuario-generalista',
        data: dadosProtocolo,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId || initialData?.id_paciente,
        empresaId: empresaId || initialData?.id_empresa,
        protocoloId: currentProtocoloId, // Usar o ID atual do estado
        statusAtendimento,
        formName: 'Prontuário Geralista'
      });

      // Retornar response junto com examesSolicitados para usar no onSuccess
      return { response, examesSolicitados };
    },
    onSuccess: async ({ response, examesSolicitados }) => {
      // Atualizar o formData com os dados retornados do backend
      let savedProtocoloId: number | undefined;
      
      console.log('✅ [ProntuarioGeralista] Resposta do salvamento:', {
        response,
        hasResult: !!response?.result,
        resultId: response?.result?.id
      });
      
      if (response?.result) {
        savedProtocoloId = response.result.id;
        
        // IMPORTANTE: Sempre atualizar o protocoloId quando receber uma resposta
        console.log('📝 [ProntuarioGeralista] Atualizando protocoloId para:', savedProtocoloId);
        setProtocoloId(savedProtocoloId);
        
        // Atualizar formData com os dados retornados (incluindo o ID)
        const updatedData = getInitialData(response.result);
        // Garantir que o ID esteja no formData
        const formDataWithId = {
          ...updatedData,
          id: savedProtocoloId
        };
        setFormData(formDataWithId);
        
        // Atualizar status se presente
        if (response.result.status_atendimento) {
          setStatusAtendimento(response.result.status_atendimento);
        }
        
        // Atualizar o componente pai através do onDataChange
        // Isso garante que na próxima vez que salvar, o ID correto será usado
        if (onDataChange) {
          onDataChange({
            ...formDataWithId,
            id: savedProtocoloId,
            id_paciente: response.result.id_paciente,
            id_atendimento: response.result.id_atendimento,
            id_empresa: response.result.id_empresa
          } as any);
        }
        
        console.log('✅ [ProntuarioGeralista] Estado atualizado com sucesso:', {
          protocoloId: savedProtocoloId,
          formDataId: formDataWithId.id
        });
      } else if (statusAtendimento) {
        // Fallback: apenas atualizar status se não houver resposta completa
        console.warn('⚠️ [ProntuarioGeralista] Resposta sem result, usando fallback');
        setFormData((prevData) => ({
          ...prevData,
          status_atendimento: statusAtendimento
        }));
        savedProtocoloId = initialData?.id || protocoloId;
        if (savedProtocoloId) {
          setProtocoloId(savedProtocoloId);
        }
      } else {
        console.error('❌ [ProntuarioGeralista] Resposta inválida do servidor:', response);
      }

      // Salvar exames solicitados via API
      if (savedProtocoloId && examesSolicitados && examesSolicitados.length > 0) {
        try {
          console.log('Salvando exames solicitados:', {
            id_protocolo_prontuario_geralista: savedProtocoloId,
            nomesExames: examesSolicitados
          });

          const result = await api.post('/api/exames_solicitados/protocolo', {
            id_protocolo_prontuario_geralista: savedProtocoloId,
            nomesExames: examesSolicitados
          });

          console.log('Exames solicitados salvos com sucesso:', result);
        } catch (error: any) {
          console.error('Erro ao salvar exames solicitados:', error);
          toast.warning("Protocolo salvo, mas houve erro ao salvar os exames solicitados.");
        }
      } else {
        console.log('Nenhum exame para salvar:', {
          savedProtocoloId,
          examesSolicitados,
          hasExames: examesSolicitados && examesSolicitados.length > 0
        });
      }

      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar prontuário generalista:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: string | boolean | string[]) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [field]: value };
      
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    // Definir campos obrigatórios para Prontuário Generalista
    const camposObrigatorios: CampoObrigatorio[] = [
      {
        campo: 'queixaPrincipal',
        nomeAmigavel: 'Queixa Principal',
        validar: (valor) => {
          return valor !== null && valor !== undefined && String(valor).trim().length > 0;
        }
      }
    ];

    if (onSave) {
      const dataWithStatus = statusAtendimento 
        ? { ...formData, status_atendimento: statusAtendimento } as any
        : formData;
      
      // Validar campos obrigatórios
      if (!validarCamposObrigatorios(dataWithStatus, camposObrigatorios)) {
        return;
      }

      try {
        await onSave(dataWithStatus);
      } catch (error) {
        console.error('Erro ao salvar documento:', error);
        toast.error("Não foi possível salvar o documento.");
      }
    } else {
      if (!atendimentoId) {
        toast.error("ID do atendimento não fornecido.");
        return;
      }

      const dataToSave = statusAtendimento 
        ? { ...formData, status_atendimento: statusAtendimento } as any
        : formData;

      // Validar campos obrigatórios
      if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
        return;
      }

      try {
        console.log('Salvando prontuário generalista:', { 
          endpoint: '/api/protocolo-prontuario-generalista', 
          data: dataToSave, 
          atendimentoId,
          examesSolicitados: dataToSave.examesSolicitados,
          hasExames: dataToSave.examesSolicitados && dataToSave.examesSolicitados.length > 0
        });
        await saveMutation.mutateAsync(dataToSave);
        console.log('Prontuário generalista salvo com sucesso');
      } catch (error) {
        console.error('Erro ao salvar prontuário generalista:', error);
        toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
      }
    }
  }, [formData, onSave, atendimentoId, statusAtendimento, saveMutation]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-prontuario-generalista/${currentProtocoloId}/imprimir`;

      console.log('Gerando PDF para visualização:', { url, protocoloId: currentProtocoloId });
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "prontuario_geralista",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Prontuário Geralista"
        value={statusAtendimento || formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          // Atualizar também no formData para manter sincronizado
          if (status) {
            setFormData((prevData) => ({
              ...prevData,
              status_atendimento: status
            }));
          }
        }}
      />
      <div>
        <Label>Queixa Principal *</Label>
        <Textarea 
          placeholder="Descreva a queixa principal do paciente..." 
          rows={3} 
          value={formData.queixaPrincipal}
          onChange={(e) => handleInputChange('queixaPrincipal', e.target.value)}
          required
        />
      </div>

      <div>
        <Label>História da Doença Atual</Label>
        <Textarea 
          placeholder="Descreva a evolução da doença atual..." 
          rows={4} 
          value={formData.historiaAtual}
          onChange={(e) => handleInputChange('historiaAtual', e.target.value)}
        />
      </div>

      <div>
        <Label>História patológica</Label>
        <Textarea 
          placeholder="Descreva o histórico patológico do paciente..." 
          rows={3} 
          value={formData.historiaPatologica}
          onChange={(e) => handleInputChange('historiaPatologica', e.target.value)}
        />
      </div>

      <div>
        <Label>História Familiar</Label>
        <Textarea 
          placeholder="Descreva o histórico familiar..." 
          rows={3} 
          value={formData.historiaFamiliar}
          onChange={(e) => handleInputChange('historiaFamiliar', e.target.value)}
        />
      </div>

      <div>
        <Label>Alergias</Label>
        <Input 
          placeholder="Descreva alergias conhecidas..." 
          value={formData.alergias}
          onChange={(e) => handleInputChange('alergias', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Faz uso de cigarros:</Label>
          <div className="flex items-center gap-4">
            <span className="text-sm">Não</span>
            <Switch
              checked={formData.usaCigarros}
              onCheckedChange={(checked) => handleInputChange('usaCigarros', checked)}
            />
            <span className="text-sm">Sim</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label>Faz uso de álcool:</Label>
          <div className="flex items-center gap-4">
            <span className="text-sm">Não</span>
            <Switch
              checked={formData.usaAlcool}
              onCheckedChange={(checked) => handleInputChange('usaAlcool', checked)}
            />
            <span className="text-sm">Sim</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label>Pratica de exercícios físicos:</Label>
          <div className="flex items-center gap-4">
            <span className="text-sm">Não</span>
            <Switch
              checked={formData.praticaExercicios}
              onCheckedChange={(checked) => handleInputChange('praticaExercicios', checked)}
            />
            <span className="text-sm">Sim</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* <Label>Exames Solicitados</Label> */}
        <ExamesSolicitacao
          initialExams={Array.isArray(formData.examesSolicitados) ? formData.examesSolicitados : []}
          onExamsChange={(exams) => handleInputChange('examesSolicitados', exams)}
          protocoloId={protocoloId}
          onSaveExames={async (exames: string[]) => {
            if (!protocoloId) {
              throw new Error('ID do protocolo não encontrado. Salve o formulário primeiro.');
            }
            
            console.log('Salvando exames ao incluir:', {
              id_protocolo_prontuario_geralista: protocoloId,
              nomesExames: exames
            });

            await api.post('/api/exames_solicitados/protocolo', {
              id_protocolo_prontuario_geralista: protocoloId,
              nomesExames: exames
            });

            // Atualizar o formData com os exames salvos
            setFormData((prevData) => ({
              ...prevData,
              examesSolicitados: exames
            }));
            setQuantidadeExames(exames.length);
          }}
        />
      </div>

      <div className="mt-6 pt-6 border-t">
        <Anexos 
          protocoloId={protocoloId}
          quantidadeExames={quantidadeExames || (Array.isArray(formData.examesSolicitados) ? formData.examesSolicitados.length : 0)}
        />
      </div>

      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`prontuario_geralista_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default ProntuarioGeralista;
